import {camelCase, deburr, join, startCase, toLower, words} from "lodash"

/**
 * Retourne un id au format camelCase composé d'un prefix et des 5 premiers mots d'un texte.
 * Exemple : getDataTestId("Etape suivante", "Bouton") -> "boutonEtapeSuivante"
 * @param {String} text un texte dont on va garder les 5 premiers mots (ou moins)
 * @param {String} prefix le prefix (optionnel)
 * @returns le data-testid au format camelCase
 */
export const getDataTestId = (text, prefix) => {
    // Si le texte est long, on garde les 5 premiers mots (et on retire les accents et les caractères spéciaux)
    let mots = words(deburr(text), /[a-zA-Z0-9]+/g).slice(0, 5)

    // On ajoute le prefix en première position s'il est défini
    if (prefix) {
        mots = [prefix, ...mots]
    }

    // On retourne l'id au format camelCase
    return camelCase(join(mots, " "))
}

/**
 * Permet de normaliser l'affichage d'un nom : PIERRE DURAND -> Pierre Durand
 * @param {String} name
 * @returns le nom avec la première lettre de chaque mot en majuscule
 */
export const normalizeNameCase = name => startCase(toLower(name))

/**
 * Normalise l'affichage d'un numéro de téléphone français.
 * Exemple : "0235151617" -> "02 35 15 16 17"
 * @param {String} value numéro de téléphone (chiffres uniquement ou avec espaces)
 * @returns le numéro formaté en groupes de 2 chiffres séparés par des espaces
 */
export const normalizeTelephoneNumber = value => {
    if (!value) return ""
    const digits = String(value).replace(/\D/g, "")
    return digits.match(/.{1,2}/g)?.join(" ") ?? ""
}

/**
 * Normalise l'affichage d'un montant en euros : espace tous les 3 chiffres, arrondi au centime
 * le plus proche, et affichage des décimales uniquement si le montant n'est pas un nombre entier.
 * Les espaces sont insécables (\u00A0) pour éviter que le montant ne soit coupé en fin de ligne (RGAA).
 * Exemples : 5 -> "5 €" / 5.2 -> "5,20 €" / 5000 -> "5 000 €" / 25.128 -> "25,13 €"
 * @param {Number} montant le montant a formater (entier ou flottant)
 * @returns le montant formaté en euros
 */
export const normalizeMontantEuro = montant => {
    if (montant === null || montant === undefined || isNaN(montant)) return ""

    // On arrondit au centime le plus proche
    const centimes = Math.round(montant * 100)
    const estUnNombreEntier = centimes % 100 === 0

    // On espace la partie entiere tous les 3 chiffres
    const partieEntiere = Math.trunc(Math.abs(centimes) / 100)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, " ")
    const partieDecimale = String(Math.abs(centimes) % 100).padStart(2, "0")
    const signe = centimes < 0 ? "-" : ""

    return estUnNombreEntier ? `${signe}${partieEntiere} €` : `${signe}${partieEntiere},${partieDecimale} €`
}
